/**
 * Общие части экранов: загрузка, пустые состояния, ошибки, «магазин не подключён», тост,
 * шапка экрана и роль.
 *
 * ⚠️ [F-4.0.11] Числа НЕ форматируются здесь. Сервер отдаёт пару
 * «значение + готовая строка», и клиент печатает строку. Иначе экран
 * и утренний отчёт покажут одно и то же число по-разному, а пост-валидатор
 * лишится точного списка допустимых числовых токенов (проверка V6).
 *
 * Тексты — по C:/tmp/rebrand/04-voice.md: что случилось → что сделать → кнопка;
 * без «внутренней кухни» (запрос, сервер, синхронизация, лимит).
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ApiResult, Session, Shown } from '../api/client.js'
import { Icon, type IconName } from '../components/icons/Icon.js'
import { backToChat } from '../telegram/call.js'
import { capitalize } from './words.js'
import artNoShop from '../assets/illustrations/noshop.webp'
import artError from '../assets/illustrations/error.webp'

/** Прочерк для неизвестного. ⛔ [F-4.0.12] Не «0»: это разные состояния. */
export const DASH = '—'

/**
 * Число сервера как есть. label — подпись значения для диктора («выручка», «маржа»): шапка
 * столбцов от него скрыта; при крупном тексте ведомость перестраивается, и подпись видна всем.
 * bare — без своей обёртки (значение уже стоит в ячейке ведомости).
 */
export function Value<T>(
  { v, sub = false, label, bare = false }: { v: Shown<T> | undefined; sub?: boolean; label?: string; bare?: boolean },
): React.JSX.Element {
  // display === null означает «данных нет». Показываем прочерк, а не ноль:
  // подставленный ноль в интерфейсе неотличим от измеренного. Диктор вместо «тире»
  // слышит «нет данных».
  const shown = v?.display ?? null
  const body = (
    <>
      {label !== undefined && <span className="vh ledger-lbl">{label} </span>}
      {shown !== null ? shown : <><span aria-hidden="true">{DASH}</span><span className="vh">нет данных</span></>}
    </>
  )
  return bare ? body : <span className={sub ? 'num-sub' : 'num'}>{body}</span>
}

// ── роль и шапка экрана ─────────────────────────────────────────────────────

/** Роль нужна пустым состояниям: владельцу — что сделать, сотруднику — кто это сделает. */
export const RoleContext = createContext<Session['role']>('staff')
export const useRole = (): Session['role'] => useContext(RoleContext)

/** Заголовок и подзаголовок шапки, если экран знает их лучше карты (имя агента, имя отчёта). */
export interface Head { title?: string | undefined; sub?: string | null | undefined }
export const HeadContext = createContext<(h: Head) => void>(() => { /* вне оболочки шапки нет */ })

export function useScreenHead(title: string | undefined, sub?: string | null): void {
  const set = useContext(HeadContext)
  useEffect(() => { set({ title, sub }) }, [set, title, sub])
}

// ── данные ──────────────────────────────────────────────────────────────────

export type State<T> = { kind: 'loading' } | { kind: 'ok'; data: T } | { kind: 'fail'; message: string }

/** Загрузка витрины с повтором: «Повторить» на экране ошибки перезапускает тот же запрос. */
export function useData<T>(load: () => Promise<ApiResult<T>>, deps: readonly unknown[] = []): { s: State<T>; reload: () => void } {
  const [s, setS] = useState<State<T>>({ kind: 'loading' })
  const [n, setN] = useState(0)
  useEffect(() => {
    let alive = true
    setS({ kind: 'loading' })
    void load().then((r) => {
      if (!alive) return
      setS(r.ok ? { kind: 'ok', data: r.data } : { kind: 'fail', message: r.error.message })
    })
    // ⚠️ Отмена обязательна: без неё ответ приходит в размонтированный
    // экран после переключения вкладки и подменяет данные другого экрана.
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, ...deps])
  const reload = useCallback(() => setN((x) => x + 1), [])
  return { s, reload }
}

// ── пустые состояния ────────────────────────────────────────────────────────

/** Иллюстрация пустого состояния — декоративная: смысл несёт заголовок рядом. */
export function Art({ src }: { src: string }): React.JSX.Element {
  return <img className="empty-art" src={src} alt="" aria-hidden="true" width={280} height={209} decoding="async" />
}

/**
 * Пустое состояние: что здесь появится → когда или от чего (04-voice, 3.8). На крупных
 * экранах — иллюстрация, в остальных — иконка раздела 32 px цвета «Пепел» (06-icons, 7.6).
 */
export function Empty(
  { art, icon, title, text, children, testid = 'empty' }:
  { art?: string; icon?: IconName; title: string; text?: string; children?: React.ReactNode; testid?: string },
): React.JSX.Element {
  return (
    <div className="empty" data-testid={testid}>
      {art !== undefined && <Art src={art} />}
      {art === undefined && icon !== undefined && <Icon name={icon} size={32} className="empty-ic" />}
      <h2 className="empty-title">{title}</h2>
      {text !== undefined && <p className="empty-text">{text}</p>}
      {children !== undefined && <div className="empty-actions">{children}</div>}
    </div>
  )
}

/** Состояние «магазин ещё не подключён» — это состояние, а не ошибка (04-voice, 3.4). */
export function NoShop({ reason }: { reason?: string | undefined }): React.JSX.Element {
  const role = useRole()
  return (
    <Empty art={artNoShop} testid="no-shop" title={capitalize(reason ?? 'магазин ещё не подключён')}
      text={role === 'owner'
        ? 'Подключите кабинет Wildberries в чате с ботом: нужен ключ, это пара минут. Потом здесь появятся выплата, штрафы, реклама и остатки.'
        : 'Его подключает владелец. Как только он это сделает, здесь появятся данные.'}>
      {role === 'owner' && (
        <button type="button" className="btn" data-testid="no-shop-chat" onClick={() => { backToChat() }}>
          Подключить в чате
        </button>
      )}
    </Empty>
  )
}

/**
 * Загрузка — скелетоны по форме экрана: место занято заранее, при ответе ничего не прыгает.
 * Диктору — скрытый текст внутри живой области («Загрузка сводки»): aria-label у живой
 * области многие дикторы не читают.
 */
export function Loading({ label, rows = 4 }: { label: string; rows?: number }): React.JSX.Element {
  return (
    <section className="card" data-testid="loading" role="status" aria-busy="true">
      <span className="vh">{label}</span>
      {Array.from({ length: rows }, (_, k) => (
        <div className="sk-row" key={k} aria-hidden="true">
          <span className="sk sk-dot" />
          <span className="sk-lines">
            <span className="sk" style={{ width: `${68 - (k % 3) * 16}%` }} />
            <span className="sk" style={{ width: `${40 + (k % 2) * 16}%`, height: 12 }} />
          </span>
        </div>
      ))}
    </section>
  )
}

/**
 * Скелетон «Сводки» по форме экрана (01, чек-лист «ничего не прыгает»): блок «Главное за утро»
 * (вывод, два пункта с метками, кнопка), список отчётов, ведомость по дням. Экран рисуется
 * целиком, только когда пришли и сводка, и отчёты, и «Главное», — блоки не вставляются сверху.
 */
export function SummarySkeleton({ lead }: { lead: boolean }): React.JSX.Element {
  const rows = (n: number, dot: boolean): React.JSX.Element[] => Array.from({ length: n }, (_, k) => (
    <div className="sk-row" key={k}>
      {dot && <span className="sk sk-dot" />}
      <span className="sk-lines"><span className="sk" style={{ width: `${56 - (k % 3) * 12}%` }} /></span>
    </div>
  ))
  return (
    <div data-testid="loading" role="status" aria-busy="true">
      <span className="vh">Загрузка сводки</span>
      <div aria-hidden="true">
        {lead && (
          <>
            {/* Карточка «Выплата WB»: сумма, изменение, две полосы, штрафы. */}
            <span className="sk sk-sect" />
            <section className="card hero">
              <span className="sk sk-amount" />
              <span className="sk sk-tag" style={{ width: 200 }} />
              <span className="sk-lines"><span className="sk" /><span className="sk" /></span>
              <span className="sk" style={{ width: '70%' }} />
            </section>
            <span className="sk sk-sect" style={{ marginTop: 24 }} />
            <section className="card brief">
              <span className="sk-lines"><span className="sk sk-h2" style={{ width: '92%' }} /><span className="sk sk-h2" style={{ width: '64%' }} /></span>
              {[0, 1].map((k) => (
                <div className="brief-item" key={k}>
                  <span className="sk sk-tag" />
                  <span className="sk-lines"><span className="sk" style={{ width: '96%' }} /><span className="sk" style={{ width: '58%' }} /></span>
                </div>
              ))}
              <span className="sk sk-btn" />
            </section>
          </>
        )}
        <span className="sk sk-sect" style={lead ? { marginTop: 24 } : undefined} />
        <section className="card">{rows(lead ? 4 : 3, true)}</section>
        <span className="sk sk-sect" style={{ marginTop: 24 }} />
        <section className="card">{rows(3, false)}</section>
      </div>
    </div>
  )
}

/**
 * Ошибка витрины. ⛔ [F-3.7.1] Внутренние причины наружу не выводятся:
 * ни код ответа, ни имя метода, ни слово «синхронизация». Сообщение
 * приходит с сервера уже нейтральным. Иконки-тревоги нет: о сбое — спокойно (06-icons, 7.6).
 */
export function Failed(
  { message, onRetry, children }: { message: string; onRetry?: () => void; children?: React.ReactNode },
): React.JSX.Element {
  return (
    <Empty art={artError} testid="failed" title="Не получилось загрузить" text={message}>
      {onRetry !== undefined && (
        <button type="button" className="btn btn-soft" data-testid="retry" onClick={onRetry}>
          <Icon name="action-retry" size={20} />Повторить
        </button>
      )}
      {children}
    </Empty>
  )
}

/**
 * Строка ошибки под формой: иконка риска своей формы + нейтральные слова. id — чтобы поле,
 * к которому относится ошибка, сослалось на неё через aria-describedby.
 */
export function InlineError({ text, testid, id }: { text: string; testid?: string; id?: string }): React.JSX.Element {
  return (
    <p className="err" role="alert" data-testid={testid} id={id}>
      <Icon name="status-risk" size={20} />
      <span>{text}</span>
    </p>
  )
}

/**
 * ⚠️ [F-3.7.2] Подпись об актуальности обязательна на ЛЮБОЙ витрине,
 * показанной при недоступном источнике. Молча показать вчерашние цифры
 * как сегодняшние запрещено.
 */
export function Freshness({ note }: { note: string | null }): React.JSX.Element | null {
  return note === null ? null : <p className="hint" data-testid="freshness">{note}</p>
}

/**
 * Блок усечения. Экран показывает выборку — человек обязан видеть, что это
 * выборка, и по какому правилу она сделана.
 */
export function Truncation({ note }: { note: string | null }): React.JSX.Element | null {
  return note === null ? null : <p className="hint" data-testid="truncation">{note}</p>
}

// ── сообщения о результате: постоянные живые области ───────────────────────

/**
 * [WCAG 4.1.3] Сообщение о результате действия («Включено…», «Сохранено», «Не получилось…»).
 *
 * ⚠️ Живая область, вставленная в DOM вместе с текстом, в VoiceOver и TalkBack зачитывается
 * ненадёжно. Поэтому обе области — «status» для удачи и «alert» для неудачи — смонтированы
 * в оболочке ВСЕГДА (Announcer), а экраны меняют только их текст через useAnnounce().
 * Удача гаснет сама через 3 с; неудача висит, пока её не закроют или не придёт новое
 * сообщение / переход на другой экран (2.2.1: без автоисчезновения).
 */
/** ok — сделано; info — справка о переходе («ссылка устарела»), без тревоги; fail — не получилось. */
export interface AnnounceMsg { kind: 'ok' | 'info' | 'fail'; text: string }
export type Announce = (m: AnnounceMsg | null) => void
export const AnnounceContext = createContext<Announce>(() => { /* вне оболочки объявлять некуда */ })
export const useAnnounce = (): Announce => useContext(AnnounceContext)

/** Состояние сообщения оболочки: id отличает одинаковые тексты подряд (диктор прочтёт оба). */
export function useAnnouncer(): { msg: (AnnounceMsg & { id: number }) | null; announce: Announce } {
  const [msg, setMsg] = useState<(AnnounceMsg & { id: number }) | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const seq = useRef(0)
  useEffect(() => () => { if (timer.current !== null) clearTimeout(timer.current) }, [])
  const announce = useCallback<Announce>((m) => {
    if (timer.current !== null) { clearTimeout(timer.current); timer.current = null }
    if (m === null) { setMsg(null); return }
    const id = ++seq.current
    setMsg({ ...m, id })
    if (m.kind !== 'fail') timer.current = setTimeout(() => setMsg((x) => (x?.id === id ? null : x)), m.kind === 'ok' ? 3000 : 4000)
  }, [])
  return { msg, announce }
}

export function Announcer(
  { msg, onClose }: { msg: (AnnounceMsg & { id: number }) | null; onClose: () => void },
): React.JSX.Element {
  const ok = msg?.kind === 'ok' || msg?.kind === 'info' ? msg : null
  const fail = msg?.kind === 'fail' ? msg : null
  return (
    <>
      <div role="status" aria-live="polite" aria-atomic="true" className={ok ? 'toast' : 'vh'}
        data-testid={ok ? 'toast' : 'live-status'}>
        {ok && <>
          {ok.kind === 'ok' ? <Icon name="status-good" size={20} className="tone-good" /> : <Icon name="section-help" size={20} className="ic-muted" />}
          <span key={ok.id}>{ok.text}</span>
        </>}
      </div>
      <div className={fail ? 'toast toast-fail' : 'vh'} data-testid={fail ? 'toast' : 'live-alert-box'}>
        <div role="alert" aria-live="assertive" aria-atomic="true" className="toast-body" data-testid="live-alert">
          {fail && <><Icon name="status-risk" size={20} className="tone-risk" /><span key={fail.id}>{fail.text}</span></>}
        </div>
        {fail && (
          <button type="button" className="toast-close" data-testid="toast-close" aria-label="Закрыть сообщение" onClick={onClose}>
            <Icon name="action-decline" size={20} />
          </button>
        )}
      </div>
    </>
  )
}
